import type { BoardData, Connection, Port } from '../model/schema.js';
import { findPort } from '../trace/ports.js';
import type { Finding, Rule, Severity } from './types.js';

/** Declares a rule; i18n keys follow `validate.<id>.title|message`. */
export function defineRule(
  id: string,
  severity: Severity,
  title: { en: string; de: string },
  check: (
    data: BoardData,
    emit: (entityIds: string[], params?: Finding['params']) => Finding,
  ) => Finding[],
): Rule {
  const messageKey = `validate.${id}.message`;
  const emit = (entityIds: string[], params: Finding['params'] = {}): Finding => ({
    ruleId: id,
    severity,
    entityIds,
    messageKey,
    params,
  });
  return {
    id,
    severity,
    title,
    titleKey: `validate.${id}.title`,
    messageKey,
    check: (data) => check(data, emit),
  };
}

export interface ResolvedConnection {
  connection: Connection;
  from: Port;
  to: Port;
}

/** Connections whose both ports exist (rules that compare ports only use these). */
export function resolvedConnections(data: BoardData): ResolvedConnection[] {
  return Object.values(data.connections).flatMap((connection) => {
    const from = findPort(data, connection.from.itemId, connection.from.portId);
    const to = findPort(data, connection.to.itemId, connection.to.portId);
    return from !== undefined && to !== undefined ? [{ connection, from, to }] : [];
  });
}

/** Entity ids for a connection finding: connection, then both items. */
export function connectionEntities(c: Connection): string[] {
  return [c.id, c.from.itemId, c.to.itemId];
}
